import { request } from './client';
import type {
  BoardTemplate,
  TemplateSquareAssignment,
  CreateTemplateInput,
  CreateTemplateFromBoardInput,
  UpdateTemplateInput,
  SquareAssignmentInput,
  CreateBoardFromTemplateInput,
  Board,
  TemplateValidationResult,
} from '../types';

export const templatesApi = {
  // List all templates owned by the current user
  async list(): Promise<BoardTemplate[]> {
    return request<BoardTemplate[]>('/api/squareup/templates');
  },

  // Get a single template by ID
  async get(id: string): Promise<BoardTemplate> {
    return request<BoardTemplate>(`/api/squareup/templates/${id}`);
  },

  // Create a new template from scratch
  async create(input: CreateTemplateInput): Promise<BoardTemplate> {
    return request<BoardTemplate>('/api/squareup/templates', {
      method: 'POST',
      body: input,
    });
  },

  // Create a template from an existing board
  async createFromBoard(boardId: string, input: CreateTemplateFromBoardInput): Promise<BoardTemplate> {
    return request<BoardTemplate>(`/api/squareup/templates/from-board/${boardId}`, {
      method: 'POST',
      body: input,
    });
  },

  // Update a template
  async update(id: string, input: UpdateTemplateInput): Promise<BoardTemplate> {
    return request<BoardTemplate>(`/api/squareup/templates/${id}`, {
      method: 'PATCH',
      body: input,
    });
  },

  // Delete a template
  async delete(id: string): Promise<void> {
    return request<void>(`/api/squareup/templates/${id}`, {
      method: 'DELETE',
    });
  },

  // Get all square assignments for a template
  async getAssignments(templateId: string): Promise<TemplateSquareAssignment[]> {
    return request<TemplateSquareAssignment[]>(`/api/squareup/templates/${templateId}/squares`);
  },

  // Set a square assignment (create or update)
  async setAssignment(templateId: string, input: SquareAssignmentInput): Promise<TemplateSquareAssignment> {
    return request<TemplateSquareAssignment>(`/api/squareup/templates/${templateId}/squares`, {
      method: 'POST',
      body: input,
    });
  },

  // Clear a square assignment
  async clearAssignment(templateId: string, row: number, col: number): Promise<void> {
    return request<void>(`/api/squareup/templates/${templateId}/squares/${row}/${col}`, {
      method: 'DELETE',
    });
  },

  // Create a new board from a template
  async createBoard(templateId: string, input: CreateBoardFromTemplateInput): Promise<Board> {
    return request<Board>(`/api/squareup/templates/${templateId}/create-board`, {
      method: 'POST',
      body: input,
    });
  },

  // Validate template assignments before creating a board
  async validateAssignments(templateId: string): Promise<TemplateValidationResult> {
    return request<TemplateValidationResult>(`/api/squareup/templates/${templateId}/validate-assignments`);
  },
};
